/**
 * La **fiche d'un acteur** — un compte du grand livre, qui le possède, ce qu'il
 * a fait, et son dossier d'identité *si le rôle a le droit de le voir*
 * (`B-11`, `EX-BO-002`, `EX-BO-004`).
 *
 * ## Ce que le porteur a demandé, mot pour mot
 *
 * « Des boutons pour aller sur une page détail de ces acteurs où on peut voir
 * son solde actuel, toutes ses transactions, ses stats, son dossier KYC s'il y
 * en a un et si le rôle connecté est censé le voir. Des cartes de stats et des
 * filtres aussi, et tout bien paginé. »
 *
 * Les six sont là : le solde, l'identité, les cartes de statistiques, la table
 * paginée de ses opérations avec ses filtres, et le dossier d'identité sous
 * condition de droit.
 *
 * ## `kycVisible` n'est pas une commodité, c'est le cœur de cet écran
 *
 * `KYC_REVIEW_QUEUE_VIEW` n'est accordé qu'à **trois rôles sur sept** —
 * `COMPLIANCE`, `SUPPORT_N2`, `AUDITOR`. La finance peut voir les opérations et
 * **pas** les dossiers d'identité, et c'est une séparation des tâches
 * (`EX-BO-002`), pas un oubli.
 *
 * **C'est le serveur qui décide, et il le dit.** Masquer côté écran n'aurait
 * rien valu — `EX-BO-002` est formel, masquer dans l'interface ne compte pas —
 * et servir le dossier à tout le monde aurait ouvert, sous `TRANSACTION_VIEW`,
 * une porte que `KYC_REVIEW_QUEUE_VIEW` ferme. La troisième voie est la bonne :
 * le serveur ne le sert pas, et il **dit** qu'il ne le sert pas.
 *
 * Trois états, donc, et ils ne se confondent pas :
 *
 *  - `kycVisible === false` — « vous n'avez pas le droit de voir » ;
 *  - `kycVisible === true` et `kyc === null` — « ce client n'a aucun dossier » ;
 *  - `kycVisible === true` et `kyc !== null` — le dossier.
 *
 * Les deux premiers se rendraient d'une case vide identique, et ce sont deux
 * faits **opposés**. C'est la distinction que le tableau de bord fait déjà entre
 * « aucune mesure » et « zéro », et c'est la même raison : un écran qui les
 * confond apprend à lire une absence de droit comme une absence de donnée.
 *
 * ## Les opérations passent par la route de la RECHERCHE
 *
 * `GET /api/backoffice/transactions?accountId=…` — pas de seconde route. Une
 * route jumelle aurait eu ses propres filtres, sa propre pagination et sa propre
 * trace, et les deux auraient divergé au premier ajustement. Les indicateurs de
 * la table suivent donc la **fenêtre** ; ceux de la fiche, eux, portent toute
 * l'histoire du compte — et les deux sont servis, jamais calculés ici.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Pagination } from '../controls/Pagination.js';
import { QueueShell } from '../shell/QueueShell.js';
import type { QueueColumn, QueueEntry } from '../shell/QueueShell.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import { fetchParty, searchTransactions } from '../http/transactions.js';
import type { PartyProfile, TransactionPage } from '../http/transactions.js';
import { StatusPill, transactionRoute, useTransactionFormats } from './transactions-display.js';

/**
 * Les cinq colonnes des opérations d'un acteur.
 *
 * Ce ne sont **pas** les sept de la recherche, et c'est délibéré : sur la fiche
 * d'un acteur, une des deux contreparties est toujours l'acteur lui-même. La
 * répéter vingt-cinq fois occuperait la colonne la plus large du tableau pour
 * dire ce que le titre de la page dit déjà. Le **sens** la remplace — envoyé ou
 * reçu —, et c'est ce qu'on vient lire sur un compte qu'on ne connaît pas.
 */
export const partyColumns: readonly QueueColumn[] = [
  { id: 'reference', labelKey: 'transactions.columns.reference', emphasis: 'numeric' },
  { id: 'time', labelKey: 'transactions.columns.time', emphasis: 'numeric' },
  { id: 'type', labelKey: 'transactions.columns.type', emphasis: 'muted' },
  { id: 'amount', labelKey: 'transactions.columns.amount', emphasis: 'numeric' },
  { id: 'status', labelKey: 'transactions.columns.status', emphasis: 'strong' },
];

/** L'écran : le compte, son propriétaire, ses statistiques, ses opérations. */
export function PartyScreen(): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();
  const { accountId } = useParams();
  const naviguer = useNavigate();
  const { montant, instant, heure, libelleType, libelleEtat, taux } = useTransactionFormats();

  const [fiche, setFiche] = useState<PartyProfile | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [operations, setOperations] = useState<TransactionPage | null>(null);
  const [erreurOperations, setErreurOperations] = useState<string | null>(null);

  useEffect(() => {
    if (accountId === undefined) return;
    void (async () => {
      try {
        setFiche(await fetchParty(client, accountId));
        setErreur(null);
      } catch (echec) {
        // La fiche lue est effacée avec l'échec : garder la précédente ferait
        // lire le solde d'un compte sous le nom d'un autre.
        setFiche(null);
        setErreur(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    })();
  }, [accountId, client]);

  const listerLesOperations = useCallback(
    async (page?: number) => {
      if (accountId === undefined) return;
      try {
        // **La route de la RECHERCHE**, avec le compte en critère. Pas de
        // seconde route : une route jumelle aurait divergé au premier
        // ajustement. La fenêtre est celle du serveur par défaut — les sept
        // derniers jours —, et l'écran dit laquelle il montre.
        setOperations(await searchTransactions(client, { accountId, page }));
        setErreurOperations(null);
      } catch (echec) {
        setOperations(null);
        setErreurOperations(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    },
    [accountId, client],
  );

  useEffect(() => {
    void listerLesOperations();
  }, [listerLesOperations]);

  const memeJournee = operations !== null && operations.from === operations.to;

  const lignes: readonly QueueEntry[] = useMemo(
    () =>
      (operations?.transactions ?? []).map((operation) => {
        // **Le sens, du point de vue de CE compte.** C'est la première question
        // qu'un contrôleur pose devant un compte qu'il ne connaît pas : ce
        // compte envoie-t-il, ou reçoit-il ?
        const envoye = operation.debit?.accountId === accountId;
        return {
          id: operation.id,
          reference: operation.publicRef,
          values: [
            operation.publicRef,
            heure(operation.initiatedAt, memeJournee),
            libelleType(operation.txType),
            montant(operation.amount, operation.currency),
            libelleEtat(operation.status),
          ],
          cells: [
            operation.publicRef,
            heure(operation.initiatedAt, memeJournee),
            <span className="transactions__flow" data-flow={envoye ? 'OUT' : 'IN'} key="type">
              <span className="transactions__flow-mark" aria-hidden="true">
                {t(envoye ? 'transactions.flow.outMark' : 'transactions.flow.inMark')}
              </span>
              {libelleType(operation.txType)}
            </span>,
            montant(operation.amount, operation.currency),
            <StatusPill key="status" status={operation.status} tone={operation.statusTone} />,
          ],
        };
      }),
    [accountId, heure, libelleEtat, libelleType, memeJournee, montant, operations, t],
  );

  const pages =
    operations === null
      ? 1
      : Math.max(1, Math.ceil(operations.total / Math.max(1, operations.pageSize)));

  if (erreur !== null) {
    return (
      <section className="transactions" aria-label={t('transactions.party.landmark')}>
        <p className="transactions__notice" role="alert">
          {t(erreur)}
        </p>
        <Link className="transactions__back" to="/transactions">
          {t('transactions.detail.back')}
        </Link>
      </section>
    );
  }

  if (fiche === null) {
    return (
      <section className="transactions" aria-label={t('transactions.party.landmark')}>
        <p className="transactions__idle">{t('transactions.party.loading')}</p>
      </section>
    );
  }

  return (
    <section className="transactions transactions--detail" aria-label={t('transactions.party.landmark')}>
      <header className="transactions__head">
        <Link className="transactions__back" to="/transactions">
          {t('transactions.detail.back')}
        </Link>
        <div className="transactions__head-title">
          <h2 className="transactions__title" data-testid="party-name">
            {nomDeLActeur(fiche, t)}
          </h2>
          <span className="transactions__status" data-tone="OTHER">
            {fiche.account.accountType}
          </span>
        </div>
        <p className="transactions__head-meta" data-testid="party-account">
          {fiche.customer === null
            ? fiche.account.accountNumber
            : t('transactions.party.headline', {
                msisdn: fiche.customer.msisdn,
                account: fiche.account.accountNumber,
                ref: fiche.customer.publicRef,
              })}
        </p>
      </header>

      {/* **Les cartes de statistiques — toutes servies, aucune calculée ici.**
          Celles-ci portent TOUTE l'histoire du compte, pas la fenêtre de la
          table en dessous : une statistique bornée à sept jours ferait dire
          « trois opérations » d'un compte qui en porte mille. */}
      <ul className="transactions__stats" aria-label={t('transactions.party.statsLandmark')}>
        <li className="transactions__stat">
          <span className="transactions__stat-label">{t('transactions.party.balance')}</span>
          <span className="transactions__stat-value" data-testid="party-balance">
            {/* Un compte de cantonnement ne tient pas de solde : nul veut dire
                « ce compte n'en tient pas », et « 0 F » se lirait comme un
                compte vidé. */}
            {fiche.account.balance === null
              ? t('transactions.party.noBalance')
              : montant(fiche.account.balance, fiche.account.currency)}
          </span>
        </li>
        <li className="transactions__stat">
          <span className="transactions__stat-label">{t('transactions.party.available')}</span>
          <span className="transactions__stat-value" data-testid="party-available">
            {fiche.account.availableBalance === null
              ? t('transactions.party.noBalance')
              : montant(fiche.account.availableBalance, fiche.account.currency)}
          </span>
        </li>
        <li className="transactions__stat">
          <span className="transactions__stat-label">{t('transactions.stats.count')}</span>
          <span className="transactions__stat-value" data-testid="party-operations">
            {fiche.stats.operations}
          </span>
        </li>
        <li className="transactions__stat">
          <span className="transactions__stat-label">{t('transactions.stats.volume')}</span>
          <span className="transactions__stat-value" data-testid="party-volume">
            {montant(fiche.stats.volume, fiche.account.currency)}
          </span>
        </li>
        <li
          className="transactions__stat"
          data-tone={fiche.stats.successRate === null ? 'OTHER' : 'SUCCEEDED'}
        >
          <span className="transactions__stat-label">
            {t('transactions.stats.successRate')}
          </span>
          <span className="transactions__stat-value" data-testid="party-success">
            {taux(fiche.stats.successRate)}
          </span>
        </li>
        <li className="transactions__stat">
          <span className="transactions__stat-label">{t('transactions.party.flow')}</span>
          <span className="transactions__stat-value" data-testid="party-flow">
            {t('transactions.party.flowValue', {
              sent: fiche.stats.debited,
              received: fiche.stats.credited,
            })}
          </span>
        </li>
      </ul>

      <div className="transactions__panels">
        {/* --- L'identité, client ou guichet --- */}
        <section className="transactions__panel">
          <h3 className="transactions__panel-title">{t('transactions.party.identityTitle')}</h3>
          <dl className="transactions__facts">
            <dt>{t('transactions.party.accountNumber')}</dt>
            <dd>{fiche.account.accountNumber}</dd>
            <dt>{t('transactions.party.accountStatus')}</dt>
            <dd data-testid="party-account-status">{fiche.account.status}</dd>
            <dt>{t('transactions.party.openedAt')}</dt>
            <dd>{instant(fiche.account.openedAt)}</dd>
            {fiche.customer === null ? null : (
              <>
                <dt>{t('transactions.party.kycLevel')}</dt>
                <dd data-testid="party-kyc-level">
                  {fiche.customer.kycLevel === null
                    ? t('transactions.party.unknown')
                    : String(fiche.customer.kycLevel)}
                </dd>
                <dt>{t('transactions.party.customerStatus')}</dt>
                <dd>{fiche.customer.status}</dd>
                <dt>{t('transactions.party.screening')}</dt>
                <dd>{fiche.customer.screeningStatus}</dd>
                <dt>{t('transactions.party.lastActivity')}</dt>
                <dd>
                  {fiche.customer.lastActivityAt === null
                    ? t('transactions.party.neverActive')
                    : instant(fiche.customer.lastActivityAt)}
                </dd>
              </>
            )}
            {fiche.outlet === null ? null : (
              <>
                <dt>{t('transactions.party.outletCode')}</dt>
                <dd data-testid="party-outlet-code">{fiche.outlet.outletCode}</dd>
                <dt>{t('transactions.party.outletCategory')}</dt>
                <dd>{fiche.outlet.category}</dd>
                <dt>{t('transactions.party.outletRegion')}</dt>
                <dd>{fiche.outlet.region ?? t('transactions.party.unknown')}</dd>
                <dt>{t('transactions.party.outletStatus')}</dt>
                <dd>{fiche.outlet.status}</dd>
              </>
            )}
          </dl>
          {fiche.customer === null && fiche.outlet === null ? (
            <p className="transactions__idle" data-testid="party-internal">
              {t('transactions.party.internalAccount')}
            </p>
          ) : null}
        </section>

        {/* --- Le dossier d'identité, SOUS CONDITION DE DROIT --- */}
        <section className="transactions__panel">
          <h3 className="transactions__panel-title">{t('transactions.party.kycTitle')}</h3>
          {!fiche.kycVisible ? (
            // **« Vous n'avez pas le droit de voir » n'est pas « il n'y a
            // rien ».** C'est le serveur qui l'a décidé, en opposant la matrice
            // à `KYC_REVIEW_QUEUE_VIEW`, et l'écran le rapporte — il ne le
            // devine pas, et il ne le tait pas.
            <p className="transactions__gap" data-testid="party-kyc-forbidden">
              {t('transactions.party.kycForbidden')}
            </p>
          ) : fiche.kyc === null ? (
            <p className="transactions__idle" data-testid="party-kyc-absent">
              {t('transactions.party.kycAbsent')}
            </p>
          ) : (
            <dl className="transactions__facts" data-testid="party-kyc">
              <dt>{t('transactions.party.kycStatus')}</dt>
              <dd data-testid="party-kyc-status">{fiche.kyc.status}</dd>
              <dt>{t('transactions.party.kycTarget')}</dt>
              <dd>{String(fiche.kyc.targetLevel)}</dd>
              <dt>{t('transactions.party.kycChannel')}</dt>
              <dd>{fiche.kyc.channel}</dd>
              <dt>{t('transactions.party.kycSubmitted')}</dt>
              <dd>{instant(fiche.kyc.submittedAt)}</dd>
              <dt>{t('transactions.party.kycDecided')}</dt>
              <dd>
                {fiche.kyc.decidedAt === null
                  ? t('transactions.party.kycPending')
                  : instant(fiche.kyc.decidedAt)}
              </dd>
              <dt>{t('transactions.party.kycReason')}</dt>
              <dd>{fiche.kyc.decisionReason ?? t('transactions.party.kycNoReason')}</dd>
            </dl>
          )}
        </section>
      </div>

      {/* --- Ses opérations, paginées par la route de la recherche --- */}
      <section className="transactions__panel transactions__panel--wide">
        <h3 className="transactions__panel-title">{t('transactions.party.operationsTitle')}</h3>
        {erreurOperations !== null ? (
          <p className="transactions__notice" role="alert">
            {t(erreurOperations)}
          </p>
        ) : operations === null ? (
          <p className="transactions__idle">{t('transactions.loading')}</p>
        ) : operations.transactions.length === 0 ? (
          <p className="transactions__idle" data-testid="party-no-operations">
            {t('transactions.party.noOperations')}
          </p>
        ) : (
          <QueueShell
            columns={partyColumns}
            entries={lignes}
            onOpen={(ligne) => {
              naviguer(transactionRoute(ligne.id));
            }}
            footer={
              <Pagination
                block="transactions"
                keyPrefix="transactions.pages"
                page={operations.page}
                pages={pages}
                pageSize={operations.pageSize}
                onGo={(page) => {
                  void listerLesOperations(page);
                }}
                summary={
                  <span className="transactions__count" data-testid="party-count">
                    {t('transactions.party.operationsCount', {
                      shown: operations.transactions.length,
                      total: operations.total,
                      from: operations.from,
                      to: operations.to,
                    })}
                  </span>
                }
              />
            }
          />
        )}
      </section>
    </section>
  );
}

/**
 * Le nom de l'acteur — client, guichet, ou compte interne.
 *
 * Un client dont la pièce n'a pas encore été déposée n'a **pas de nom
 * enregistré**, et l'écran le dit : c'est exactement la population du pilote, et
 * le cas qui a coûté `D-95`. Une case vide se lirait comme une donnée perdue.
 */
function nomDeLActeur(
  fiche: PartyProfile,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (fiche.customer !== null) {
    return fiche.customer.displayName ?? t('transactions.cells.unnamedCustomer');
  }
  if (fiche.outlet !== null) return fiche.outlet.businessName;
  return fiche.account.accountNumber;
}
